// Libraries
import { useCallback, useLayoutEffect, useRef, useState } from 'react';

/**
 * Custom hook to track the scroll position of a specified HTML element.
 * @returns Object containing the component reference and scroll positions.
 */
export const useTrackScroll = <T extends HTMLElement>() => {
  const [scrollTopPosition, setScrollTopPosition] = useState(0);
  const [scrollBottomPosition, setScrollBottomPosition] = useState(0);
  const componentRef = useRef<T>(null);

  /**
   * Event handler for the scroll event.
   * Updates the scroll positions based on the element's properties.
   */
  const handleScroll = useCallback(() => {
    const element = componentRef.current;

    if (element) {
      const top = element?.scrollTop;
      const bottom = Math.abs(element?.scrollHeight - element?.scrollTop - element?.clientHeight);

      setScrollTopPosition(top);
      setScrollBottomPosition(bottom);
    }
  }, []);

  // Attach scroll event listener when the component is mounted or updated.
  useLayoutEffect(() => {
    const element = componentRef.current;
    element?.addEventListener('scroll', handleScroll, { passive: true });

    // Cleanup: remove scroll event listener when the component is unmounted or updated.
    return () => {
      element?.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll, componentRef]);

  return { componentRef, scrollTopPosition, scrollBottomPosition };
};
